import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";

interface GuideOption {
  label: string;
  templateId?: string;
  followup?: GuideStep;
}

interface GuideStep {
  question: string;
  options: GuideOption[];
}

interface GuidedCreationDialogProps {
  onTemplateSelect: (templateId: string) => void;
  onClose: () => void;
  visible: boolean;
}

const GuidedCreationDialog: React.FC<GuidedCreationDialogProps> = ({
  onTemplateSelect,
  onClose,
  visible,
}) => {
  const [currentStep, setCurrentStep] = useState<GuideStep | null>(null);
  const [history, setHistory] = useState<GuideStep[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (visible) {
      loadGuideScript();
    }
  }, [visible]);

  const loadGuideScript = async () => {
    try {
      const response = await fetch("/Data/guided-creation-script.json");
      const script: GuideStep = await response.json();
      setCurrentStep(script);
      setHistory([]);
      setLoading(false);
    } catch (error) {
      console.error("Error loading guide script:", error);
      setLoading(false);
    }
  };

  const handleOptionSelect = (option: GuideOption) => {
    if (option.templateId) {
      // Final selection - load template
      onTemplateSelect(option.templateId);
    } else if (option.followup) {
      // Navigate to next step
      if (currentStep) {
        setHistory([...history, currentStep]);
      }
      setCurrentStep(option.followup);
    }
  };

  const handleBack = () => {
    if (history.length > 0) {
      const previousStep = history[history.length - 1];
      setCurrentStep(previousStep);
      setHistory(history.slice(0, -1));
    }
  };

  const handleSkip = () => {
    // Skip guide and start with blank template
    onClose();
  };

  if (!visible || loading || !currentStep) {
    return null;
  }

  const footerContent = (
    <div className="guided-creation-footer">
      {history.length > 0 && (
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-button-text"
          onClick={handleBack}
        />
      )}
      <Button
        label="Skip & Start Blank"
        className="p-button-text p-button-secondary"
        onClick={handleSkip}
      />
    </div>
  );

  return (
    <Dialog
      header={currentStep.question}
      visible={visible}
      onHide={onClose}
      footer={footerContent}
      draggable
      resizable={false}
      style={{ width: "500px" }}
      className="guided-creation-dialog"
      breakpoints={{ "960px": "75vw", "641px": "95vw" }}
      position="right"
    >
      <div className="guided-creation-options">
        {currentStep.options.map((option, index) => (
          <Button
            key={index}
            label={option.label}
            className="guided-creation-option-button"
            onClick={() => handleOptionSelect(option)}
          />
        ))}
      </div>
    </Dialog>
  );
};

export default GuidedCreationDialog;
